/**
 * Browser-history / URL / session persistence for the search widget.
 *
 * Pure helpers (no React, no globals except what is passed in) so they can be
 * unit-tested in a plain node environment. The widget-side orchestration lives
 * in `hooks/useSearchHistory.ts`.
 *
 * Three cooperating layers:
 *  - history.state marker  → "this history entry belongs to the open widget",
 *                            so Back closes the modal and Forward/Back-from-a-
 *                            product reopens it.
 *  - URL params `bsq`/`bsf` → the query and filters survive a hard reload and
 *                            are shareable. Namespaced so they can't collide
 *                            with the host page's own params.
 *  - sessionStorage        → scroll position and how many pages were loaded,
 *                            which don't belong in a shareable URL.
 */
import type { SearchFilters, SearchResponse } from './types';

export const URL_QUERY_PARAM = 'bsq';
export const URL_FILTERS_PARAM = 'bsf';
export const HISTORY_MARKER_KEY = 'botiliaSearch';
export const SESSION_STATE_KEY = 'botilia_search_state';
export const SESSION_SNAPSHOT_KEY = 'botilia_search_snapshot';
/** Don't try to persist absurdly large result payloads (sessionStorage quota is ~5MB). */
export const MAX_SNAPSHOT_BYTES = 1_000_000;
/** Session state older than this is ignored (the user is unlikely to be "returning"). */
export const SESSION_STATE_TTL_MS = 30 * 60 * 1000;

export interface RestorableSearchState {
  query: string;
  filters: SearchFilters;
}

export interface PersistedSearchState extends RestorableSearchState {
  /** Path of the host page the widget was open on. */
  path: string;
  /** Number of products that were loaded (restore re-fetches this many). */
  loadedCount: number;
  /** scrollTop of the widget's results container. */
  scrollTop: number;
  /** Epoch ms when saved. */
  ts: number;
}

/** Minimal Storage shape so tests can pass a Map-backed fake. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** window.sessionStorage, or null when unavailable (SSR, blocked, sandboxed). */
export function getSessionStorageSafe(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/*  Filters <-> string                                                 */
/* ------------------------------------------------------------------ */

const FILTER_VALUE_TYPES = new Set(['string', 'number', 'boolean']);

/** Drop undefined/empty entries and sort keys so equal filter sets serialize identically. */
export function normalizeFilters(filters: SearchFilters | null | undefined): SearchFilters {
  const out: SearchFilters = {};
  if (!filters) return out;
  for (const key of Object.keys(filters).sort()) {
    const v = filters[key];
    if (v === undefined || v === null || v === '') continue;
    if (!FILTER_VALUE_TYPES.has(typeof v)) continue;
    out[key] = v;
  }
  return out;
}

export function hasAnyFilter(filters: SearchFilters | null | undefined): boolean {
  return Object.keys(normalizeFilters(filters)).length > 0;
}

export function filtersEqual(a: SearchFilters | null | undefined, b: SearchFilters | null | undefined): boolean {
  return JSON.stringify(normalizeFilters(a)) === JSON.stringify(normalizeFilters(b));
}

/** Compact JSON of the active filters, or null when there are none. */
export function serializeFilters(filters: SearchFilters | null | undefined): string | null {
  const normalized = normalizeFilters(filters);
  return Object.keys(normalized).length > 0 ? JSON.stringify(normalized) : null;
}

/** Inverse of serializeFilters. Tolerates garbage (returns {}). */
export function parseFilters(raw: string | null | undefined): SearchFilters {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return normalizeFilters(parsed as SearchFilters);
  } catch {
    return {};
  }
}

/* ------------------------------------------------------------------ */
/*  URL search string                                                  */
/* ------------------------------------------------------------------ */

/** Returns a new `?a=b` search string with the widget params written in (or removed when empty). */
export function writeSearchParams(search: string, state: RestorableSearchState | null): string {
  const params = new URLSearchParams(search);
  params.delete(URL_QUERY_PARAM);
  params.delete(URL_FILTERS_PARAM);
  if (state) {
    const q = state.query.trim();
    if (q) params.set(URL_QUERY_PARAM, q);
    const f = serializeFilters(state.filters);
    if (f) params.set(URL_FILTERS_PARAM, f);
  }
  const str = params.toString();
  return str ? `?${str}` : '';
}

/** Reads the widget params out of a search string; null when neither is present/meaningful. */
export function readSearchParams(search: string): RestorableSearchState | null {
  const params = new URLSearchParams(search);
  const query = (params.get(URL_QUERY_PARAM) ?? '').trim();
  const filters = parseFilters(params.get(URL_FILTERS_PARAM));
  if (!query && !hasAnyFilter(filters)) return null;
  return { query, filters };
}

/** Same as writeSearchParams(search, null): strips our params only. */
export function stripSearchParams(search: string): string {
  return writeSearchParams(search, null);
}

/* ------------------------------------------------------------------ */
/*  history.state marker                                               */
/* ------------------------------------------------------------------ */

export function hasHistoryMarker(state: unknown): boolean {
  return !!state && typeof state === 'object' && (state as Record<string, unknown>)[HISTORY_MARKER_KEY] === true;
}

/** Merge the marker into an existing (host page's) state object without clobbering it. */
export function withHistoryMarker(state: unknown): Record<string, unknown> {
  const base = state && typeof state === 'object' && !Array.isArray(state)
    ? { ...(state as Record<string, unknown>) }
    : {};
  base[HISTORY_MARKER_KEY] = true;
  return base;
}

/** Remove the marker; returns null when nothing else was in the state. */
export function withoutHistoryMarker(state: unknown): Record<string, unknown> | null {
  if (!state || typeof state !== 'object' || Array.isArray(state)) return null;
  const rest = { ...(state as Record<string, unknown>) };
  delete rest[HISTORY_MARKER_KEY];
  return Object.keys(rest).length > 0 ? rest : null;
}

/* ------------------------------------------------------------------ */
/*  sessionStorage                                                     */
/* ------------------------------------------------------------------ */

export function saveSessionState(storage: StorageLike | null | undefined, state: Omit<PersistedSearchState, 'ts'>): void {
  if (!storage) return;
  try {
    const record: PersistedSearchState = {
      ...state,
      filters: normalizeFilters(state.filters),
      ts: Date.now(),
    };
    storage.setItem(SESSION_STATE_KEY, JSON.stringify(record));
  } catch {
    /* storage blocked / quota — persistence is best-effort */
  }
}

export function clearSessionState(storage: StorageLike | null | undefined): void {
  try {
    storage?.removeItem(SESSION_STATE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Loads the saved state for `path`. Returns null when missing, for another
 * page, malformed, or older than SESSION_STATE_TTL_MS.
 */
export function loadSessionState(
  storage: StorageLike | null | undefined,
  path: string,
  now: number = Date.now(),
): PersistedSearchState | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(SESSION_STATE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const p = parsed as Record<string, unknown>;
    if (p.path !== path) return null;
    if (typeof p.ts !== 'number' || now - p.ts > SESSION_STATE_TTL_MS) return null;
    const query = typeof p.query === 'string' ? p.query : '';
    const filters = normalizeFilters((p.filters ?? {}) as SearchFilters);
    const loadedCount = typeof p.loadedCount === 'number' && p.loadedCount > 0 ? Math.floor(p.loadedCount) : 0;
    const scrollTop = typeof p.scrollTop === 'number' && p.scrollTop > 0 ? p.scrollTop : 0;
    return { query, filters, path, loadedCount, scrollTop, ts: p.ts };
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/*  Resolving what to restore                                          */
/* ------------------------------------------------------------------ */

export interface RestoreDecision extends RestorableSearchState {
  loadedCount: number;
  scrollTop: number;
}

/**
 * Decide what to reopen with. The URL wins for query/filters (it is what the
 * history entry/shared link says); the session record contributes scroll and
 * page depth only when it describes the same search, otherwise they'd restore
 * the scroll of a different result set.
 */
export function resolveRestoreState(
  fromUrl: RestorableSearchState | null,
  session: PersistedSearchState | null,
): RestoreDecision | null {
  const base = fromUrl ?? (session ? { query: session.query, filters: session.filters } : null);
  if (!base) return null;
  if (!base.query.trim() && !hasAnyFilter(base.filters)) return null;
  const sameSearch = !!session
    && session.query.trim() === base.query.trim()
    && filtersEqual(session.filters, base.filters);
  return {
    query: base.query,
    filters: normalizeFilters(base.filters),
    loadedCount: sameSearch ? session!.loadedCount : 0,
    scrollTop: sameSearch ? session!.scrollTop : 0,
  };
}

/* ------------------------------------------------------------------ */
/*  Results snapshot (so Back paints the results in the first frame)   */
/* ------------------------------------------------------------------ */

export interface ResultsSnapshot {
  query: string;
  filters: SearchFilters;
  results: SearchResponse;
  ts: number;
}

/** Persist the rendered results for `query`+`filters`. Oversized payloads are skipped. */
export function saveResultsSnapshot(
  storage: StorageLike | null | undefined,
  snap: { query: string; filters: SearchFilters; results: SearchResponse },
): void {
  if (!storage) return;
  try {
    const record: ResultsSnapshot = {
      query: snap.query.trim(),
      filters: normalizeFilters(snap.filters),
      results: snap.results,
      ts: Date.now(),
    };
    const json = JSON.stringify(record);
    if (json.length > MAX_SNAPSHOT_BYTES) {
      storage.removeItem(SESSION_SNAPSHOT_KEY);
      return;
    }
    storage.setItem(SESSION_SNAPSHOT_KEY, json);
  } catch {
    /* quota / blocked — best-effort */
  }
}

export function clearResultsSnapshot(storage: StorageLike | null | undefined): void {
  try {
    storage?.removeItem(SESSION_SNAPSHOT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Returns the snapshot only when it was taken for exactly this query+filters
 * and is younger than SESSION_STATE_TTL_MS; otherwise null (caller fetches).
 */
export function loadResultsSnapshot(
  storage: StorageLike | null | undefined,
  query: string,
  filters: SearchFilters,
  now: number = Date.now(),
): { results: SearchResponse; ts: number } | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(SESSION_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const p = parsed as Partial<ResultsSnapshot>;
    if (typeof p.ts !== 'number' || now - p.ts > SESSION_STATE_TTL_MS) return null;
    if ((p.query ?? '') !== query.trim()) return null;
    if (!filtersEqual(p.filters, filters)) return null;
    const r = p.results;
    if (!r || typeof r !== 'object' || !Array.isArray(r.products)) return null;
    return { results: r, ts: p.ts };
  } catch {
    return null;
  }
}
